import { playUIClick } from "@/audio/SoundManager";

interface ChatEntry {
  userId: string;
  name: string;
  text: string;
  ts: number;
}

const MAX_MESSAGES = 20;
const MESSAGE_TTL_MS = 60_000;

export class GroupChat {
  private container = document.getElementById("group-chat")!;
  private toggleBtn = document.getElementById("group-chat-toggle")!;
  private body = document.getElementById("group-chat-body")!;
  private messagesEl = document.getElementById("group-chat-messages")!;
  private emptyEl = document.getElementById("group-chat-empty")!;
  private unreadBadge = this.toggleBtn.querySelector(".chat-unread") as HTMLElement;

  private messages: ChatEntry[] = [];
  private selfId = "";
  private unread = 0;
  private nameMap = new Map<string, string>();
  private pruneTimer: ReturnType<typeof setInterval> | null = null;

  constructor() {
    this.toggleBtn.addEventListener("click", () => this.toggle());
    this.pruneTimer = setInterval(() => this.pruneExpired(), 5_000);
  }

  setSelfId(id: string): void {
    this.selfId = id;
  }

  setPlayerName(userId: string, name: string): void {
    this.nameMap.set(userId, name);
  }

  removePlayer(userId: string): void {
    this.nameMap.delete(userId);
  }

  get isOpen(): boolean {
    return this.container.classList.contains("open");
  }

  toggle(): void {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
    playUIClick();
  }

  open(): void {
    this.container.classList.add("open");
    this.clearUnread();
    this.scrollToBottom();
  }

  close(): void {
    this.container.classList.remove("open");
  }

  addMessage(userId: string, text: string): void {
    const entry: ChatEntry = {
      userId,
      name: this.nameMap.get(userId) ?? userId.slice(0, 6),
      text,
      ts: Date.now(),
    };

    this.messages.push(entry);
    if (this.messages.length > MAX_MESSAGES) {
      this.messages.shift();
      this.messagesEl.firstElementChild?.remove();
    }

    this.emptyEl.style.display = "none";
    this.messagesEl.appendChild(this.renderEntry(entry));
    this.scrollToBottom();

    if (!this.isOpen && userId !== this.selfId) {
      this.unread++;
      this.updateBadge();
    }
  }

  private renderEntry(entry: ChatEntry): HTMLElement {
    const row = document.createElement("div");
    row.className = entry.userId === this.selfId ? "chat-msg chat-msg-self" : "chat-msg";

    const time = document.createElement("span");
    time.className = "chat-msg-time";
    const d = new Date(entry.ts);
    time.textContent = `${d.getHours().toString().padStart(2, "0")}:${d.getMinutes().toString().padStart(2, "0")}`;

    const name = document.createElement("span");
    name.className = "chat-msg-name";
    name.textContent = entry.name;
    name.style.color = entry.userId === this.selfId ? "" : this.hashColor(entry.userId);

    const text = document.createElement("span");
    text.className = "chat-msg-text";
    text.textContent = entry.text;

    row.appendChild(time);
    row.appendChild(name);
    row.appendChild(text);
    return row;
  }

  private scrollToBottom(): void {
    requestAnimationFrame(() => {
      this.messagesEl.scrollTop = this.messagesEl.scrollHeight;
    });
  }

  private clearUnread(): void {
    this.unread = 0;
    this.updateBadge();
  }

  private updateBadge(): void {
    if (this.unread > 0) {
      this.unreadBadge.textContent = this.unread > 99 ? "99+" : String(this.unread);
      this.unreadBadge.classList.add("visible");
    } else {
      this.unreadBadge.classList.remove("visible");
    }
  }

  private pruneExpired(): void {
    const now = Date.now();
    let removed = 0;
    while (this.messages.length > 0 && now - this.messages[0]!.ts > MESSAGE_TTL_MS) {
      this.messages.shift();
      this.messagesEl.firstElementChild?.remove();
      removed++;
    }
    if (removed > 0 && this.messages.length === 0) {
      this.emptyEl.style.display = "";
    }
  }

  /** Deterministic color from userId for non-self players. */
  private hashColor(userId: string): string {
    let h = 0;
    for (let i = 0; i < userId.length; i++) {
      h = ((h << 5) - h + userId.charCodeAt(i)) | 0;
    }
    const hue = ((h % 360) + 360) % 360;
    return `hsl(${hue}, 60%, 70%)`;
  }
}
